import React, { useState, useEffect, useRef } from 'react';
import './A3Builder.css';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Environment } from '../utils/Environment';
import { authProvider } from '../utils/authProvider';
import { useToast } from '../layout/Layout';
import {
  FIELD_LABELS,
  SECTION_HEADINGS,
  PLACEHOLDERS,
  EMPTY_STATE_MESSAGES,
  BUTTON_LABELS,
  UI_TEXT,
  API_KEYS,
  SUCCESS_MESSAGES,
  ERROR_MESSAGES,
  REQUIRED_FIELDS,
  DEFAULT_VALUES
} from './constants/A3Constants';
import {
  genId,
  formatDate,
  formatCurrency,
  getStatusClass,
  breakLongWords,
  isFieldEmpty,
  getPlaceholderForLabel,
  extractRowData
} from './utils/A3Utils';
import {
  createCauseRow,
  createCountermeasureRow,
  createActionRow,
  createLessonRow,
  createProjectInfoFields,
  createDefineProblemFields,
  createCurrentStateFields,
  createGoalFields
} from './utils/FieldFactory';

type FieldType = 'text' | 'textarea' | 'dropdown' | 'date' | 'number' | 'row';

interface Field {
  id: string;
  label: string;
  type: FieldType;
  value?: any;
  options?: string[]; // for dropdown
  children?: Field[]; // for row
}

interface Section {
  id: string;
  heading: string;
  content: string;
  fields: Field[];
}

const initialSections: Section[] = [
  {
    id: genId('s1'),
    heading: SECTION_HEADINGS.PROJECT_INFO,
    content: '',
    fields: createProjectInfoFields(),
  },
  { 
    id: genId('s2'), 
    heading: SECTION_HEADINGS.DEFINE_PROBLEM, 
    content: '', 
    fields: createDefineProblemFields()
  },
  { 
    id: genId('s3'), 
    heading: SECTION_HEADINGS.CURRENT_STATE, 
    content: '', 
    fields: createCurrentStateFields()
  },
  { 
    id: genId('s4'), 
    heading: SECTION_HEADINGS.GOAL_TARGET_STATE, 
    content: '', 
    fields: createGoalFields()
  },
  { id: genId('s5'), heading: SECTION_HEADINGS.CAUSE_ANALYSIS, content: '', fields: [] },
  { id: genId('s6'), heading: SECTION_HEADINGS.COUNTERMEASURES, content: '', fields: [] },
  { id: genId('s7'), heading: SECTION_HEADINGS.ACTION_PLAN, content: '', fields: [] },
  { id: genId('s8'), heading: SECTION_HEADINGS.RESULTS_LESSONS, content: '', fields: [] },
];

const A3Builder: React.FC = () => {
  const [templateName] = useState('');
  const [sections, setSections] = useState<Section[]>(initialSections);
  const [preview, setPreview] = useState(false);
  const [generatedJson] = useState('');
  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [activeTab, setActiveTab] = useState<'form' | 'preview'>('form');
  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Edit mode: read a3Id from query string
  const searchParams = new URLSearchParams(location.search);
  const editA3Id = searchParams.get('a3Id');
  const [editTitle, setEditTitle] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(false);

  // Load existing A3 data when editing
  useEffect(() => {
    if (!editA3Id) return;
    const load = async () => {
      try {
        const apiUrl = `${Environment.viewA3FormAPI()}?a3Id=${editA3Id}`;
        const res = await axios.get<any>(apiUrl);
        const data = res.data;
        setIsEditMode(true);
        setEditTitle(data.title || '');
        // Map API response back into sections
        setSections(prev => prev.map(sec => {
          if (sec.heading === SECTION_HEADINGS.PROJECT_INFO) {
            return {
              ...sec, fields: sec.fields.map(f => {
                if (f.label === FIELD_LABELS.TITLE) return { ...f, value: data.title || '' };
                if (f.label === FIELD_LABELS.INDUSTRIAL_ENGINEER) return { ...f, value: data.industrialEngineer || '' };
                if (f.label === FIELD_LABELS.SITE_DC) return { ...f, value: data.siteDc || '' };
                if (f.label === FIELD_LABELS.REGION) return { ...f, value: data.region || '' };
                if (f.label === FIELD_LABELS.STATUS) return { ...f, value: data.status || '' };
                if (f.label === FIELD_LABELS.DATE) return { ...f, value: data.date || '' };
                if (f.label === FIELD_LABELS.VALIDATED_SAVINGS) return { ...f, value: data.validatedSavings != null ? String(data.validatedSavings) : '' };
                if (f.label === FIELD_LABELS.TAGS) return { ...f, value: data.tags || '' };
                return f;
              })
            };
          }
          if (sec.heading === SECTION_HEADINGS.DEFINE_PROBLEM) {
            return {
              ...sec, fields: sec.fields.map(f => {
                if (f.label === FIELD_LABELS.BACKGROUND) return { ...f, value: data.defineProblem?.background || '' };
                if (f.label === FIELD_LABELS.PROBLEM_STATEMENT) return { ...f, value: data.defineProblem?.problemStatement || '' };
                return f;
              })
            };
          }
          if (sec.heading === SECTION_HEADINGS.CURRENT_STATE) {
            return {
              ...sec, fields: sec.fields.map(f => {
                if (f.label === FIELD_LABELS.DESCRIPTION) return { ...f, value: data.currentState?.description || '' };
                if (f.label === FIELD_LABELS.METRIC_1) return { ...f, value: data.currentState?.metric1 || '' };
                if (f.label === FIELD_LABELS.METRIC_1_UNFAVORABLE) return { ...f, value: data.currentState?.metric1Unfavorable ? 'true' : '' };
                if (f.label === FIELD_LABELS.METRIC_2) return { ...f, value: data.currentState?.metric2 || '' };
                if (f.label === FIELD_LABELS.METRIC_2_UNFAVORABLE) return { ...f, value: data.currentState?.metric2Unfavorable ? 'true' : '' };
                return f;
              })
            };
          }
          if (sec.heading === SECTION_HEADINGS.GOAL_TARGET_STATE) {
            return {
              ...sec, fields: sec.fields.map(f => {
                if (f.label === FIELD_LABELS.GOAL_STATEMENT) return { ...f, value: data.goalTargetState?.goalStatement || '' };
                if (f.label === FIELD_LABELS.TARGET_METRIC) return { ...f, value: data.goalTargetState?.targetMetric || '' };
                if (f.label === FIELD_LABELS.TARGET_DATE) return { ...f, value: data.goalTargetState?.targetDate || '' };
                return f;
              })
            };
          }
          if (sec.heading === SECTION_HEADINGS.CAUSE_ANALYSIS && data.causeAnalysis?.length) {
            const rows: Field[] = data.causeAnalysis.map((c: any) => createCauseRow(c.cause, c.category, c.evidence));
            return { ...sec, fields: rows };
          }
          if (sec.heading === SECTION_HEADINGS.COUNTERMEASURES && data.countermeasures?.length) {
            const rows: Field[] = data.countermeasures.map((c: any) => createCountermeasureRow(c.countermeasure, c.linkedCause, c.owner));
            return { ...sec, fields: rows };
          }
          if (sec.heading === SECTION_HEADINGS.ACTION_PLAN && data.actionPlan?.length) {
            const rows: Field[] = data.actionPlan.map((a: any) => createActionRow(a.action, a.owner, a.dueDate, a.status));
            return { ...sec, fields: rows };
          }
          if (sec.heading === SECTION_HEADINGS.RESULTS_LESSONS && data.resultsAndLessonsLearned) {
            const rll = data.resultsAndLessonsLearned;
            const lessonRows: Field[] = (rll.lessonsLearned || []).map((l: any) => createLessonRow(l.lesson, l.category));
            return { ...sec, content: rll.resultsSummary || '', fields: lessonRows };
          }
          return sec;
        }));
      } catch (err) {
        console.error(err);
        showError(ERROR_MESSAGES.LOAD_FAILED);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editA3Id]);

  // Reset to new form when navigating away from edit mode
  useEffect(() => {
    if (!editA3Id) {
      // Reset all state to initial values for new form
      setSections(initialSections);
      setIsEditMode(false);
      setEditTitle('');
      setActiveTab('form');
      setPreview(false);
    }
  }, [editA3Id]);

  // Scroll to top whenever the location changes (page is navigated to)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const updateSection = (id: string, patch: Partial<Section>) => {
    setSections(s => s.map(x => x.id === id ? { ...x, ...patch } : x));
  };

  const addCauseRow = (sectionId: string) => {
    setSections(prev => prev.map(sec => {
      if (sec.id !== sectionId) return sec;
      return { ...sec, fields: [...sec.fields, createCauseRow()] };
    }));
  };

  const addCountermeasureRow = (sectionId: string) => {
    setSections(prev => prev.map(sec => {
      if (sec.id !== sectionId) return sec;
      return { ...sec, fields: [...sec.fields, createCountermeasureRow()] };
    }));
  };

  const addActionRow = (sectionId: string) => {
    setSections(prev => prev.map(sec => {
      if (sec.id !== sectionId) return sec;
      return { ...sec, fields: [...sec.fields, createActionRow()] };
    }));
  };

  const addLessonRow = (sectionId: string) => {
    setSections(prev => prev.map(sec => {
      if (sec.id !== sectionId) return sec;
      return { ...sec, fields: [...sec.fields, createLessonRow()] };
    }));
  };

  const updateField = (sectionId: string, fieldId: string, patch: Partial<Field>) => {
    setSections(prev => prev.map(sec => {
      if (sec.id !== sectionId) return sec;
      return { ...sec, fields: sec.fields.map(f => f.id === fieldId ? { ...f, ...patch } : f) };
    }));
  };

  const updateRowChild = (sectionId: string, rowId: string, childId: string, patch: Partial<Field>) => {
    setSections(prev => prev.map(sec => {
      if (sec.id !== sectionId) return sec;
      return {
        ...sec,
        fields: sec.fields.map(f => {
          if (f.id !== rowId || !f.children) return f;
          return { ...f, children: f.children!.map(c => c.id === childId ? { ...c, ...patch } : c) };
        })
      };
    }));
  };

  const removeRow = (sectionId: string, rowId: string) => {
    setSections(prev => prev.map(sec => {
      if (sec.id !== sectionId) return sec;
      return { ...sec, fields: sec.fields.filter(f => f.id !== rowId) };
    }));
  };

  // Helper: Get row CSS class based on section heading
  const getRowClassName = (sectionHeading: string): string => {
    if (sectionHeading === 'Action Plan') return 'action-row';
    if (sectionHeading === 'Countermeasures') return 'counter-row';
    if (sectionHeading === 'Results & Lessons Learned') return 'lesson-row';
    return 'cause-row';
  };

  // Helper: Get grid template columns based on section heading
  const getGridTemplateColumns = (sectionHeading: string): string => {
    if (sectionHeading === 'Action Plan') return '2fr 1fr 1fr 1fr';
    if (sectionHeading === 'Results & Lessons Learned') return '2fr 1fr';
    return '2fr 1fr 2fr';
  };

  // Helper: Get placeholder text for textarea fields
  const getTextareaPlaceholder = (label: string): string => {
    if (label === 'Background') return "Context - why does this matter, what's the setting?";
    if (label === 'Problem Statement') return "What's wrong, how big is the gap, since when?";
    if (label === 'Description') return 'How does the process work today?';
    if (label === 'Goal Statement') return 'What does success look like?';
    return '';
  };

  // Helper: Get placeholder text for metric fields
  const getMetricPlaceholder = (label: string): string => {
    if (label === 'Metric 1') return 'e.g. 2.3 hrs avg detention';
    if (label === 'Metric 2 (optional)') return 'e.g. $38,400 in fees';
    return '';
  };

  // Helper: Render row field with children
  const renderRowField = (
    sectionId: string,
    sectionHeading: string,
    field: Field
  ) => {
    return (
      <div className="a3-row-container">
        <div 
          className={getRowClassName(sectionHeading)} 
          style={{ 
            flex: 1, 
            display: 'grid', 
            gridTemplateColumns: getGridTemplateColumns(sectionHeading), 
            gap: 12 
          }}
        >
          {(field.children || []).map(c => (
            <div key={c.id}>
              {c.type === 'text' && <input className="input" value={c.value || ''} placeholder={c.label} onChange={e => updateRowChild(sectionId, field.id, c.id, { value: e.target.value })} />}
              {c.type === 'date' && <input className="input" type="text" value={c.value || ''} placeholder={c.label} onChange={e => updateRowChild(sectionId, field.id, c.id, { value: e.target.value })} />}
              {c.type === 'dropdown' && (
                <select className="input" value={c.value || (c.options && c.options[0]) || ''} onChange={e => updateRowChild(sectionId, field.id, c.id, { value: e.target.value })}>
                  {(c.options||[]).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
        <button
          title="Remove row"
          onClick={() => removeRow(sectionId, field.id)}
          style={{
            flexShrink: 0,
            background: 'none',
            border: '1px solid #fca5a5',
            borderRadius: 6,
            color: '#dc2626',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            padding: '6px 10px',
            marginBottom: 2,
          }}
        >✕</button>
      </div>
    );
  };

  // Helper: Render target metric field with target date
  const renderTargetMetricField = (
    sectionId: string,
    section: Section,
    field: Field
  ) => {
    const targetDateField = section.fields.find(x => x.label === 'Target Date');
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label className="form-label">Target Metric</label>
          <input 
            className="input" 
            value={field.value || ''} 
            placeholder="e.g. Target: < 1.0 hr avg detention" 
            onChange={e => updateField(sectionId, field.id, { value: e.target.value })} 
          />
        </div>
        {targetDateField && (
          <div>
            <label className="form-label">{targetDateField.label}</label>
            <input 
              className="input" 
              value={targetDateField.value || ''} 
              placeholder="e.g. 30-Sep-2026" 
              onChange={e => updateField(sectionId, targetDateField.id, { value: e.target.value })} 
            />
          </div>
        )}
      </div>
    );
  };

  // Helper: Render metric field with unfavorable flag
  const renderMetricField = (
    sectionId: string,
    section: Section,
    field: Field
  ) => {
    const isMetric = field.label === 'Metric 1' || field.label === 'Metric 2 (optional)';
    const flagLabel = field.label === 'Metric 1' ? 'Metric 1 Unfavorable' : 'Metric 2 Unfavorable';
    const flagField = isMetric ? section.fields.find(x => x.label === flagLabel) : undefined;

    return (
      <div className="a3-flex-center-gap">
        <input 
          className="input a3-metric-input" 
          value={field.value || ''} 
          placeholder={getMetricPlaceholder(field.label)} 
          onChange={e => updateField(sectionId, field.id, { value: e.target.value })} 
        />
        {isMetric && flagField && (
          <label className="a3-metric-flag-label">
            <input
              type="checkbox"
              checked={flagField.value === 'true'}
              onChange={e => updateField(sectionId, flagField.id, { value: e.target.checked ? 'true' : '' })}
              className="a3-metric-flag-checkbox"
            />
            Flag metric {field.label === 'Metric 1' ? '1' : '2'} as unfavorable (red)
          </label>
        )}
      </div>
    );
  };

  // Ensure textareas fit their content on mount / when sections change
  useEffect(() => {
    // run in next tick so DOM updates are applied
    requestAnimationFrame(() => {
      document.querySelectorAll<HTMLTextAreaElement>('.textarea.autogrow').forEach(ta => {
        ta.style.height = 'auto';
        ta.style.height = `${ta.scrollHeight}px`;
      });
    });
  }, [sections]);

  // Close modal on Escape and lock background scroll
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreview(false);
    };
    // do not lock body scrolling; allow page to scroll if preview content grows
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [preview]);

  // Position the floating close button near the modal's top-right edge
  useEffect(() => {
    if (!preview) return;
    const positionClose = () => {
      const modal = modalRef.current;
      const btn = closeRef.current;
      if (!modal || !btn) return;
      const rect = modal.getBoundingClientRect();
      // place the button slightly overlapping the modal's top-right corner
      const top = Math.max(8, rect.top + 8);
      const left = Math.min(window.innerWidth - 12, rect.right - 48);
      btn.style.position = 'fixed';
      btn.style.top = `${top}px`;
      btn.style.left = `${left}px`;
      // ensure right is unset so left takes precedence
      btn.style.right = 'auto';
    };

    positionClose();
    window.addEventListener('resize', positionClose);
    window.addEventListener('scroll', positionClose, true);
    return () => {
      window.removeEventListener('resize', positionClose);
      window.removeEventListener('scroll', positionClose, true);
    };
  }, [preview]);

  const generate = () => {
    // Open preview only; JSON generation is handled elsewhere if needed
    void setPreview(true);
  };
  void generate;

  // Build the payload object from current form state
  const buildPayload = () => {
    // Find the Title field within Project Info (the first/Project Info section)
    const projectInfoSection = sections.find(s => s.heading === SECTION_HEADINGS.PROJECT_INFO);
    const titleField = projectInfoSection?.fields.find(f => f.label === FIELD_LABELS.TITLE);
    const currentTitle = titleField?.value || '';
    
    // Find individual fields inside Project Info section to build flat keys
    const getFieldValue = (label: string) => 
      projectInfoSection?.fields.find(f => f.label === label)?.value || '';

    // Find sections by heading
    const findSection = (heading: string) => sections.find(s => s.heading === heading);
    
    // Define the Problem section
    const problemSection = findSection(SECTION_HEADINGS.DEFINE_PROBLEM);
    const defineProblem = {
      background: problemSection?.fields.find(f => f.label === FIELD_LABELS.BACKGROUND)?.value || '',
      problemStatement: problemSection?.fields.find(f => f.label === FIELD_LABELS.PROBLEM_STATEMENT)?.value || ''
    };

    // Current State section
    const currentStateSection = findSection(SECTION_HEADINGS.CURRENT_STATE);
    const currentState = {
      description: currentStateSection?.fields.find(f => f.label === FIELD_LABELS.DESCRIPTION)?.value || '',
      metric1: currentStateSection?.fields.find(f => f.label === FIELD_LABELS.METRIC_1)?.value || '',
      metric1Unfavorable: currentStateSection?.fields.find(f => f.label === FIELD_LABELS.METRIC_1_UNFAVORABLE)?.value === 'true',
      metric2: currentStateSection?.fields.find(f => f.label === FIELD_LABELS.METRIC_2)?.value || '',
      metric2Unfavorable: currentStateSection?.fields.find(f => f.label === FIELD_LABELS.METRIC_2_UNFAVORABLE)?.value === 'true'
    };

    // Goal / Target State section
    const goalSection = findSection(SECTION_HEADINGS.GOAL_TARGET_STATE);
    const goalTargetState = {
      goalStatement: goalSection?.fields.find(f => f.label === FIELD_LABELS.GOAL_STATEMENT)?.value || '',
      targetMetric: goalSection?.fields.find(f => f.label === FIELD_LABELS.TARGET_METRIC)?.value || '',
      targetDate: goalSection?.fields.find(f => f.label === FIELD_LABELS.TARGET_DATE)?.value || ''
    };

    // Cause Analysis section
    const causeSection = findSection(SECTION_HEADINGS.CAUSE_ANALYSIS);
    const causeAnalysis = extractRowData(causeSection?.fields || [], {
      cause: FIELD_LABELS.CAUSE,
      category: FIELD_LABELS.CATEGORY,
      evidence: FIELD_LABELS.EVIDENCE
    });

    // Countermeasures section
    const cmSection = findSection(SECTION_HEADINGS.COUNTERMEASURES);
    const countermeasures = extractRowData(cmSection?.fields || [], {
      countermeasure: FIELD_LABELS.COUNTERMEASURE,
      linkedCause: FIELD_LABELS.LINKED_CAUSE,
      owner: FIELD_LABELS.OWNER
    });

    // Action Plan section
    const actionSection = findSection(SECTION_HEADINGS.ACTION_PLAN);
    const actionPlan = (actionSection?.fields || [])
      .filter(f => f.type === 'row')
      .map(row => ({
        action: row.children?.find(c => c.label === FIELD_LABELS.ACTION)?.value || '',
        owner: row.children?.find(c => c.label === FIELD_LABELS.OWNER)?.value || '',
        dueDate: row.children?.find(c => /due/i.test(c.label))?.value || '',
        status: row.children?.find(c => /status/i.test(c.label))?.value || ''
      }));

    // Results & Lessons Learned section
    const resultsSection = findSection(SECTION_HEADINGS.RESULTS_LESSONS);
    const lessonsLearned = extractRowData(resultsSection?.fields || [], {
      lesson: FIELD_LABELS.LESSON,
      category: FIELD_LABELS.CATEGORY
    });

    const resultsAndLessonsLearned = {
      resultsSummary: resultsSection?.content || '',
      lessonsLearned
    };

    // Build the payload mapping matching what the A3Detail component expects
    return { 
      a3Id: editA3Id || '',
      name: currentTitle || '', 
      title: currentTitle || '',
      industrialEngineer: getFieldValue(FIELD_LABELS.INDUSTRIAL_ENGINEER),
      siteDc: getFieldValue(FIELD_LABELS.SITE_DC),
      region: getFieldValue(FIELD_LABELS.REGION),
      status: getFieldValue(FIELD_LABELS.STATUS),
      date: getFieldValue(FIELD_LABELS.DATE),
      validatedSavings: getFieldValue(FIELD_LABELS.VALIDATED_SAVINGS),
      tags: getFieldValue(FIELD_LABELS.TAGS),
      defineProblem,
      currentState,
      goalTargetState,
      causeAnalysis,
      countermeasures,
      actionPlan,
      resultsAndLessonsLearned
    };
  };

  // Insert zero-width spaces into very long words so they can wrap in the preview
  // This function is now imported from A3Utils

  const handleRefresh = () => {
    setShowRefreshConfirm(true);
  };

  const confirmRefresh = () => {
    // Reset to initial state
    setSections(initialSections);
    setActiveTab('form');
    setPreview(false);
    setShowRefreshConfirm(false);
    showSuccess('Form has been refreshed successfully!');
  };

  const cancelRefresh = () => {
    setShowRefreshConfirm(false);
  };

  const exportToStorage = async () => {
    // ensure preview is closed when exporting
    setPreview(false);
    
    const payload = generatedJson ? JSON.parse(generatedJson) : buildPayload();
    const auth = authProvider();
    const userId = auth.userId || DEFAULT_VALUES.GUEST_USER;

    // Validation: Check required fields before sending to API
    const requiredFields = [
      { key: API_KEYS.TITLE, label: FIELD_LABELS.TITLE, value: payload.title },
      { key: API_KEYS.INDUSTRIAL_ENGINEER, label: FIELD_LABELS.INDUSTRIAL_ENGINEER, value: payload.industrialEngineer },
      { key: API_KEYS.SITE_DC, label: FIELD_LABELS.SITE_DC, value: payload.siteDc },
      { key: API_KEYS.REGION, label: FIELD_LABELS.REGION, value: payload.region },
      { key: API_KEYS.STATUS, label: FIELD_LABELS.STATUS, value: payload.status },
      { key: API_KEYS.DATE, label: FIELD_LABELS.DATE, value: payload.date },
      { key: API_KEYS.TAGS, label: FIELD_LABELS.TAGS, value: payload.tags }
    ];

    const missingFields = requiredFields.filter(({ value }) => isFieldEmpty(value));
    
    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(f => f.label).join(', ');
      showError(`${ERROR_MESSAGES.MISSING_FIELDS}${fieldNames}`);
      // Switch to form tab to show the user where to fill
      setActiveTab('form');
      return; // Stop execution - don't send to API
    }

    try {
      let res;
      if (isEditMode && editA3Id) {
        const apiUrl = `${Environment.editA3FormAPI()}?${API_KEYS.A3_ID}=${editA3Id}&${API_KEYS.USER_ID}=${userId}`;
        res = await axios.post(apiUrl, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        const apiUrl = `${Environment.saveA3FormAPI()}?${API_KEYS.USER_ID}=${userId}`;
        res = await axios.post(apiUrl, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      }
      
      if (res.status === 200 || res.status === 201) {
        showSuccess(isEditMode ? SUCCESS_MESSAGES.UPDATED : SUCCESS_MESSAGES.SAVED);
        
        // Extract a3Id from response and navigate to detail page
        const savedA3Id = res.data?.a3Id || editA3Id;
        setTimeout(() => {
          if (savedA3Id) {
            navigate(`/wms-upload-ui/ie-hub/a3-repository/${savedA3Id}`);
          } else {
            // Fallback to repository if no ID is available
            navigate('/wms-upload-ui/ie-hub/a3-repository');
          }
        }, 1500);
      } else {
        showError(`${ERROR_MESSAGES.EXPORT_FAILED}${res.statusText}`);
      }
    } catch (err: any) {
      const errMsg = err?.response?.data || err?.message || err || ERROR_MESSAGES.UNKNOWN_ERROR;
      showError(`${ERROR_MESSAGES.EXPORT_ERROR}${errMsg}`);
    }
  };

  return (
    <div className="a3-container">
      <h2 className="a3-title"><strong>{isEditMode ? UI_TEXT.EDIT_A3 : UI_TEXT.NEW_A3}</strong></h2>
      <p className="a3-subtitle">
        {isEditMode
          ? <>{UI_TEXT.EDITING} <strong>{editTitle || UI_TEXT.THIS_A3}</strong>{UI_TEXT.EDITING_SAVE_MESSAGE}</>
          : UI_TEXT.NEW_A3_INSTRUCTION
        }
      </p>

      <div className="builder-tabs">
        <div className="tab-links">
          <button
            className={`btab ${activeTab === 'form' ? 'active' : ''}`}
            onClick={() => setActiveTab('form')}
          >
            Form
          </button>

          <button
            className={`btab ${activeTab === 'preview' ? 'active' : ''}`}
            onClick={() => setActiveTab('preview')}
          >
            Preview
          </button>
        </div>

        
          <div className="tab-actions">
            <button className="btn form-button cancel-button" onClick={() => navigate('/wms-upload-ui/ie-hub/a3-repository')}>{BUTTON_LABELS.CANCEL}</button>
            {activeTab === 'form' && (
              <button 
                className="btn form-button a3-refresh-button" 
                onClick={handleRefresh} 
                title="Reset form to initial state"
              >
                <svg className="a3-refresh-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10"></polyline>
                  <polyline points="23 20 23 14 17 14"></polyline>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                </svg>
                {BUTTON_LABELS.REFRESH}
              </button>
            )}
            <button className="btn form-button" onClick={exportToStorage}>{BUTTON_LABELS.SAVE_A3}</button>
          </div>
      </div>

  {activeTab === 'form' && (
  <div className="edit-form">
    {/* Project Info - render as a single form */}
    <section className="b-section project-info section-card">
      <h3 className="section-heading-button"><span className="num">1</span>{SECTION_HEADINGS.PROJECT_INFO}</h3>
      {(() => {
        const sec = sections[0];
        const fld = (label: string) => sec?.fields.find(f => f.label === label);
        // Define which fields are required
        const requiredFieldLabels = REQUIRED_FIELDS;
        
        const renderField = (f: any) => {
          if (!f) return null;
          const isRequired = requiredFieldLabels.includes(f.label);
          return (
            <div key={f.id} className="a3-field-container">
              <label className="form-label">
                {f.label}
                {isRequired && <span className="a3-required-asterisk">*</span>}
              </label>
              {f.type === 'text' && <input className="input" value={f.value || ''} placeholder={getPlaceholderForLabel(f.label, PLACEHOLDERS)} onChange={e => updateField(sec.id, f.id, { value: e.target.value })} />}
              {f.type === 'dropdown' && (
                <select className="input" value={f.value || (f.options && f.options[0]) || ''} onChange={e => updateField(sec.id, f.id, { value: e.target.value })}>
                  {(f.options || []).map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              )}
              {f.type === 'date' && (
                <input
                  className="input"
                  type="text"
                  value={f.value || ''}
                  placeholder={PLACEHOLDERS.DATE}
                  onChange={e => updateField(sec.id, f.id, { value: e.target.value })}
                  onBlur={e => {
                    const formatted = formatDate(e.target.value.trim());
                    if (formatted !== e.target.value) {
                      updateField(sec.id, f.id, { value: formatted });
                    }
                  }}
                />
              )}
              {f.type === 'number' && <input className="input" type="text" value={f.value ?? ''} placeholder={PLACEHOLDERS.VALIDATED_SAVINGS} onChange={e => updateField(sec.id, f.id, { value: e.target.value })} />}
            </div>
          );
        };
        return (
          <div className="a3-flex-col-gap">
            {/* Row 1: Title — full width */}
            <div>{renderField(fld(FIELD_LABELS.TITLE))}</div>
            {/* Row 2: Industrial Engineer | Site/DC */}
            <div className="a3-grid-2col">
              {renderField(fld(FIELD_LABELS.INDUSTRIAL_ENGINEER))}
              {renderField(fld(FIELD_LABELS.SITE_DC))}
            </div>
            {/* Row 3: Region | Status */}
            <div className="a3-grid-2col">
              {renderField(fld(FIELD_LABELS.REGION))}
              {renderField(fld(FIELD_LABELS.STATUS))}
            </div>
            {/* Row 4: Date | Validated Savings */}
            <div className="a3-grid-2col">
              {renderField(fld(FIELD_LABELS.DATE))}
              {renderField(fld(FIELD_LABELS.VALIDATED_SAVINGS))}
            </div>
            {/* Row 5: Tags — full width */}
            <div>{renderField(fld(FIELD_LABELS.TAGS))}</div>
          </div>
        );
      })()}
    </section>

    {/* Render remaining sections (Define the Problem, Current State, Goal/Target, etc.) */}
    {(sections || []).slice(1).map((s, idx) => (
      <section key={s.id} className={`b-section section-card a3-mt-16`}>
        <h3 className="section-heading-button"><span className="num">{idx + 2}</span>{s.heading}</h3>
        {s.heading === SECTION_HEADINGS.CAUSE_ANALYSIS && s.fields.filter(f => f.type === 'row').length === 0 && (
          <p className="a3-section-description">
            {EMPTY_STATE_MESSAGES.NO_CAUSES}
          </p>
        )}
        {s.heading === SECTION_HEADINGS.COUNTERMEASURES && s.fields.filter(f => f.type === 'row').length === 0 && (
          <p className="a3-section-description">
            {EMPTY_STATE_MESSAGES.NO_COUNTERMEASURES}
          </p>
        )}
        {s.heading === SECTION_HEADINGS.ACTION_PLAN && s.fields.filter(f => f.type === 'row').length === 0 && (
          <p className="a3-section-description">
            {EMPTY_STATE_MESSAGES.NO_ACTIONS}
          </p>
        )}
        <div className="form-group">
          {/* Row-based sections with no rows yet: show add button only */}
          {['Cause Analysis', 'Countermeasures', 'Action Plan', 'Results & Lessons Learned'].includes(s.heading) &&
           s.fields.filter(f => f.type === 'row').length === 0 && (
            <div className="a3-grid-gap-mb">
              {/* Results & Lessons Learned: show Results Summary textarea, then hint, then button */}
              {s.heading === 'Results & Lessons Learned' && (
                <>
                  <div>
                    <label className="form-label">Results Summary</label>
                    <textarea className="textarea autogrow" rows={6} value={s.content || ''} placeholder="What happened after the countermeasures went live?" onChange={e => updateSection(s.id, { content: e.target.value })} />
                  </div>
                  <p className="a3-section-description-no-margin">
                    No lessons yet.
                  </p>
                </>
              )}
              {/* Add button — full width, same height as a real row */}
              <div>
                {s.heading === 'Cause Analysis' && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => addCauseRow(s.id)}
                    style={{
                      width: '100%',
                      minHeight: 38,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 700,
                      border: '2px dashed #94a3b8',
                      background: '#f8fafc',
                      borderRadius: 8,
                      cursor: 'pointer',
                      color: '#0b314a',
                    }}
                  >
                    <span className="a3-add-button-icon">+</span>Add Cause
                  </button>
                )}
                {s.heading === 'Countermeasures' && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => addCountermeasureRow(s.id)}
                    style={{
                      width: '100%',
                      minHeight: 38,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 700,
                      border: '2px dashed #94a3b8',
                      background: '#f8fafc',
                      borderRadius: 8,
                      cursor: 'pointer',
                      color: '#0b314a',
                    }}
                  >
                    <span className="a3-add-button-icon">+</span>Add Countermeasure
                  </button>
                )}
                {s.heading === 'Action Plan' && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => addActionRow(s.id)}
                    style={{
                      width: '100%',
                      minHeight: 38,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 700,
                      border: '2px dashed #94a3b8',
                      background: '#f8fafc',
                      borderRadius: 8,
                      cursor: 'pointer',
                      color: '#0b314a',
                    }}
                  >
                    <span className="a3-add-button-icon">+</span>Add Action
                  </button>
                )}
                {s.heading === 'Results & Lessons Learned' && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => addLessonRow(s.id)}
                    style={{
                      width: '100%',
                      minHeight: 38,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 700,
                      border: '2px dashed #94a3b8',
                      background: '#f8fafc',
                      borderRadius: 8,
                      cursor: 'pointer',
                      color: '#0b314a',
                    }}
                  >
                    <span className="a3-add-button-icon">+</span>Add Lesson
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Normal rendering when fields exist */}
            {s.fields && s.fields.length > 0 ? (
            <div className="a3-grid-gap">
                {/* Results & Lessons Learned: show Results Summary content first */}
                {s.heading === 'Results & Lessons Learned' && (
                  <div>
                    <label className="form-label">Results Summary</label>
                    <textarea className="textarea autogrow" rows={6} value={s.content || ''} placeholder="What happened after the countermeasures went live?" onChange={e => updateSection(s.id, { content: e.target.value })} />
                  </div>
                )}
              {s.fields.map(f => {
                // Skip hidden flag fields
                if (f.label.endsWith('Unfavorable')) return null;
                
                // Skip Target Date field (rendered with Target Metric)
                if (f.label === 'Target Date') return null;

                return (
                  <div key={f.id}>
                    {f.type === 'row' ? (
                      renderRowField(s.id, s.heading, f)
                    ) : (
                      <div>
                        {f.label !== 'Target Metric' && <label className="form-label">{f.label}</label>}
                        {f.type === 'textarea' && (
                          <textarea 
                            className="textarea autogrow" 
                            rows={6} 
                            value={f.value || ''} 
                            placeholder={getTextareaPlaceholder(f.label)} 
                            onChange={e => updateField(s.id, f.id, { value: e.target.value })} 
                          />
                        )}
                        {f.type === 'text' && !f.label.endsWith('Unfavorable') && (
                          f.label === 'Target Metric' 
                            ? renderTargetMetricField(s.id, s, f)
                            : renderMetricField(s.id, s, f)
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* If this is the Cause Analysis section, show Add Cause button */}
              {s.heading === 'Cause Analysis' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => addCauseRow(s.id)}
                  style={{
                    width: '100%', minHeight: 38, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 700,
                    border: '2px dashed #94a3b8', background: '#f8fafc', borderRadius: 8,
                    cursor: 'pointer', color: '#0b314a',
                  }}
                >
                  <span className="a3-add-button-icon">+</span>Add Cause
                </button>
              )}

              {s.heading === 'Countermeasures' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => addCountermeasureRow(s.id)}
                  style={{
                    width: '100%', minHeight: 38, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 700,
                    border: '2px dashed #94a3b8', background: '#f8fafc', borderRadius: 8,
                    cursor: 'pointer', color: '#0b314a',
                  }}
                >
                  <span className="a3-add-button-icon">+</span>Add Countermeasure
                </button>
              )}

              {s.heading === 'Action Plan' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => addActionRow(s.id)}
                  style={{
                    width: '100%', minHeight: 38, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 700,
                    border: '2px dashed #94a3b8', background: '#f8fafc', borderRadius: 8,
                    cursor: 'pointer', color: '#0b314a',
                  }}
                >
                  <span className="a3-add-button-icon">+</span>Add Action
                </button>
              )}

              {s.heading === 'Results & Lessons Learned' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => addLessonRow(s.id)}
                  style={{
                    width: '100%', minHeight: 38, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 700,
                    border: '2px dashed #94a3b8', background: '#f8fafc', borderRadius: 8,
                    cursor: 'pointer', color: '#0b314a',
                  }}
                >
                  <span className="a3-add-button-icon">+</span>Add Lesson
                </button>
              )}
            </div>
          ) : (
            !['Cause Analysis', 'Countermeasures', 'Action Plan', 'Results & Lessons Learned'].includes(s.heading) &&
            <textarea className="textarea autogrow" rows={8} value={s.content || ''} onChange={e => updateSection(s.id, { content: e.target.value })} />
          )}
        </div>
      </section>
    ))}
  </div>
)}
      

{activeTab === 'preview' && (() => {
  // build preview data
  const proj = sections[0];
  const getProj = (label: string) => proj?.fields.find(f => f.label === label)?.value;
  const savings = getProj('Validated Savings ($, optional)');

  const find = (h: string) => sections.find(s => s.heading === h);
  const problem = find('Define the Problem');
  const current = find('Current State');
  const goal = find('Goal / Target State');
  const causesSec = find('Cause Analysis');
  const cmSec = find('Countermeasures');
  const actionsSec = find('Action Plan');
  const resultSec = find('Results & Lessons Learned');

  const rowsFrom = (sec?: Section) => (sec?.fields || []).filter(f => f.type === 'row');

  const causes = rowsFrom(causesSec).map(r => ({
    cause: r.children?.find(c => c.label === 'Cause')?.value || '',
    category: r.children?.find(c => c.label === 'Category')?.value || '',
    evidence: r.children?.find(c => c.label === 'Evidence')?.value || ''
  }));

  const cms = rowsFrom(cmSec).map(r => ({
    cm: r.children?.find(c => c.label === 'Countermeasure')?.value || '',
    cause: r.children?.find(c => c.label === 'Linked Cause')?.value || '',
    owner: r.children?.find(c => c.label === 'Owner')?.value || ''
  }));

  const actions = rowsFrom(actionsSec).map(r => ({
    action: r.children?.find(c => c.label === 'Action')?.value || '',
    owner: r.children?.find(c => c.label === 'Owner')?.value || '',
    due: r.children?.find(c => /due/i.test(c.label))?.value || '',
    status: r.children?.find(c => /status/i.test(c.label))?.value || ''
  }));

  const lessons = rowsFrom(resultSec).map(r => ({
    lesson: r.children?.find(c => c.label === 'Lesson')?.value || '',
    category: r.children?.find(c => c.label === 'Category')?.value || ''
  }));

  const resultsSummary = resultSec?.content || '';

  // layout: left column - 1,2,3,6,7 ; right column - 4,5
  return (
    <div className="a3sheet" style={{ overflow: 'auto' }}>
      <div style={{ transform: 'scale(1)', transformOrigin: 'top left', width: '100%' }}>
        {/* Project Info inline preview header: Industrial Engineer, Site / DC, Date, Status, Title (single line with underline) */}
        <div className="b-section">
          {(() => {
            const findField = (label: string) => proj?.fields.find(f => f.label === label);
            const val = (label: string) => findField(label)?.value ?? '';

            return (
              <div className="preview-inline-header" role="group" aria-label="Project info inline header">
                {/* Title value-only on the left */}
                <div className="preview-inline-item title-only a3-text-left">
                  <div className="preview-inline-value title-value a3-text-left">{val('Title') || templateName || DEFAULT_VALUES.UNTITLED_A3}</div>
                  <div className="a3-preview-subtitle">{UI_TEXT.DRAFT_PREVIEW_SUBTITLE}</div>
                </div>

                {/* Right grouped items */}
                <div className="preview-inline-right">
                  <div className="preview-inline-item">
                    <label>Industrial Engineer</label>
                    <div className="preview-inline-value a3-min-width-80">{val('Industrial Engineer') || '\u00a0'}</div>
                  </div>

                  <div className="preview-inline-item">
                    <label>Site / DC</label>
                    <div className="preview-inline-value a3-min-width-60">{val('Site/DC') || '\u00a0'}</div>
                  </div>

                  <div className="preview-inline-item">
                    <label>Date</label>
                    <div className="preview-inline-value a3-min-width-80">{val('Date') || '\u00a0'}</div>
                  </div>

                  <div className="preview-inline-item">
                    <label>Status</label>
                    <div className="preview-inline-value">{val('Status') ? <span className={`status-badge ${getStatusClass(val('Status'))}`}>{val('Status')}</span> : <span className="placeholder">{EMPTY_STATE_MESSAGES.EMPTY}</span>}</div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="a3grid">
          <div className="left-col">
            {/* 1 Define the Problem */}
            <div className="box">
              <h3 className="section-heading-button"><span className="num">1</span>{SECTION_HEADINGS.DEFINE_PROBLEM}</h3>
              <div className="box-content">{problem?.fields?.find(f => f.label === FIELD_LABELS.PROBLEM_STATEMENT)?.value || <span className="a3-not-documented">{EMPTY_STATE_MESSAGES.NOT_DOCUMENTED}</span>}</div>
              {problem?.fields?.find(f => f.label === FIELD_LABELS.BACKGROUND)?.value && (
                <div className="box-content a3-mt-8"><strong>{UI_TEXT.BACKGROUND_LABEL}</strong> {problem?.fields?.find(f => f.label === FIELD_LABELS.BACKGROUND)?.value}</div>
              )}
            </div>

            {/* 2 Current State */}
            <div className="box">
              <h3 className="section-heading-button"><span className="num">2</span>{SECTION_HEADINGS.CURRENT_STATE}</h3>
              <div className="box-content">{current?.fields?.find(f => f.label === FIELD_LABELS.DESCRIPTION)?.value || <span className="a3-not-documented">{EMPTY_STATE_MESSAGES.NOT_DOCUMENTED}</span>}</div>
              {(() => {
                const m1 = current?.fields?.find(f => f.label === 'Metric 1');
                const m2 = current?.fields?.find(f => f.label === 'Metric 2 (optional)');
                const m1bad = current?.fields?.find(f => f.label === 'Metric 1 Unfavorable')?.value === 'true';
                const m2bad = current?.fields?.find(f => f.label === 'Metric 2 Unfavorable')?.value === 'true';
                if (!m1?.value && !m2?.value) return null;
                return (
                  <div className="metric-strip a3-mt-8">
                    {m1?.value && <span className={`metric-chip${m1bad ? ' bad' : ''}`}><strong>{m1.value}</strong></span>}
                    {m2?.value && <span className={`metric-chip${m2bad ? ' bad' : ''}`}><strong>{m2.value}</strong></span>}
                  </div>
                );
              })()}
            </div>

            {/* 3 Goal / Target State */}
            <div className="box">
              <h3 className="section-heading-button"><span className="num">3</span>{SECTION_HEADINGS.GOAL_TARGET_STATE}</h3>
              <div className="box-content">{goal?.fields?.find(f => f.label === FIELD_LABELS.GOAL_STATEMENT)?.value || <span className="a3-not-documented">{EMPTY_STATE_MESSAGES.NOT_DOCUMENTED}</span>}</div>
              {(() => {
                const targetMetric = goal?.fields?.find(f => f.label === 'Target Metric');
                const targetDate = goal?.fields?.find(f => f.label === 'Target Date');
                if (!targetMetric?.value && !targetDate?.value) return null;
                return (
                  <div className="metric-strip" style={{ marginTop: 8 }}>
                    {targetMetric?.value && <span className="metric-chip good"><strong>{targetMetric.value}</strong></span>}
                    {targetDate?.value && <span className="metric-chip"><strong>{targetDate.value}</strong></span>}
                  </div>
                );
              })()}
            </div>

            {/* action and results moved to full-width rows below */}
          </div>

          <div className="right-col">
            {/* 4 Cause Analysis */}
            <div className="box">
              <h3 className="section-heading-button"><span className="num">4</span>{SECTION_HEADINGS.CAUSE_ANALYSIS}</h3>
              {causes.length ? (
                <table className="a3table"><thead><tr><th>Cause</th><th>Category</th><th>Evidence</th></tr></thead><tbody>
                  {causes.map((c) => <tr key={`cause-${c.cause}-${c.category}`}><td>{c.cause}</td><td>{c.category}</td><td>{c.evidence}</td></tr>)}
                </tbody></table>
              ) : <p className="placeholder">{EMPTY_STATE_MESSAGES.NO_CAUSES_LOGGED}</p>}
            </div>

            {/* 5 Countermeasures */}
            <div className="box">
              <h3 className="section-heading-button"><span className="num">5</span>{SECTION_HEADINGS.COUNTERMEASURES}</h3>
              {cms.length ? (
                <table className="a3table"><thead><tr><th>Countermeasure</th><th>Linked Cause</th><th>Owner</th></tr></thead><tbody>
                  {cms.map((c) => <tr key={`cm-${c.cm}-${c.owner}`}><td>{c.cm}</td><td>{c.cause}</td><td>{c.owner}</td></tr>)}
                </tbody></table>
              ) : <p className="placeholder">{EMPTY_STATE_MESSAGES.NO_COUNTERMEASURES_LOGGED}</p>}
            </div>
          </div>
        </div>

        {/* Full-width Action Plan (6) spanning both columns */}
        <div className="full-width-row a3-mt-12">
          <div className="box">
            <h3 className="section-heading-button"><span className="num">6</span>{SECTION_HEADINGS.ACTION_PLAN}</h3>
            {actions.length ? (
              <table className="a3table">
                <colgroup>
                  <col style={{ width: '48%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '16%' }} />
                </colgroup>
                <thead><tr><th>Action</th><th>Owner</th><th>Due Date</th><th>Status</th></tr></thead><tbody>
                {actions.map((a) => (
                  <tr key={`action-${a.action}-${a.owner}-${a.due}`}><td>{a.action}</td><td>{a.owner}</td><td>{a.due}</td><td><span className={`status-badge ${getStatusClass(a.status)}`}>{a.status}</span></td></tr>
                ))}
              </tbody></table>
            ) : <p className="placeholder">{EMPTY_STATE_MESSAGES.NO_ACTIONS_LOGGED}</p>}
          </div>
        </div>

        {/* Full-width Results & Lessons Learned (7) spanning both columns */}
        <div className="full-width-row a3-mt-12">
          <div className="box results-box-flex-container">
            {/* Left: heading + content + table */}
            <div className="results-box-content results-box-padding">
              <h3 className="section-heading-button results-heading-margin"><span className="num">7</span>{SECTION_HEADINGS.RESULTS_LESSONS}</h3>
              <div className="box-content">{resultsSummary || <span className="a3-not-documented">{EMPTY_STATE_MESSAGES.NOT_DOCUMENTED}</span>}</div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 8 }}>
                {/* Lessons table */}
                <div style={{ flex: 1 }}>
                  {lessons.length ? (
                    <table className="a3table"><thead><tr><th>Lesson</th><th>Category</th></tr></thead><tbody>
                      {lessons.map((l) => <tr key={`lesson-${l.lesson}-${l.category}`}><td>{l.lesson}</td><td>{l.category}</td></tr>)}
                    </tbody></table>
                  ) : null}
                </div>
                {/* Right: savings card aligned with table - height based on number of rows */}
                <div style={{ flexShrink: 0, width: 200 }}>
                  {(savings !== undefined && savings !== null && savings !== '') ? (
                    <div className="savings-card-dark" style={{ 
                      height: lessons.length > 0 ? 'auto' : undefined,
                      minHeight: lessons.length > 0 ? `${40 + (lessons.length * 44)}px` : undefined 
                    }}>
                      <div className="scd-amt">{formatCurrency(savings) || savings}</div>
                      <div className="scd-lbl">{UI_TEXT.ANNUALIZED_SAVINGS}</div>
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      minHeight: lessons.length > 0 ? `${40 + (lessons.length * 44)}px` : 80, 
                      background: '#e2e8f0', borderRadius: 8, padding: '16px 20px', textAlign: 'center',
                    }}>
                      <span style={{ fontStyle: 'normal', fontWeight: 700, fontSize: 13, color: '#64748b' }}>Savings not yet recorded</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
})()}

      {preview && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="a3-modal" tabIndex={-1} ref={modalRef}>
            <div className="a3-modal-header">
              <h3 className="preview-title">{templateName || 'Untitled A3'}</h3>
            </div>

            <div className="a3-sheet">
              <div className="preview-block a3-sheet-inner">
                <div className="a3-page">
                  <div className="a3-page-accent" />
                  <header className="a3-header">
                    <div>
                      <h2 className="a3-title-large">{templateName || 'Untitled A3'}</h2>
                    </div>
                  </header>

                  <main className="a3-body">
                    {sections.map((s, i) => (
                      <article key={s.id} className="a3-section-card">
                        <h3 className="section-heading-button"><span className="num">{i + 1}</span>{s.heading || `Heading ${i + 1}`}</h3>
                        <div className="a3-section-content">{breakLongWords(s.content) || '(empty)'}</div>
                        {s.fields && s.fields.length > 0 && (
                          <div className="a3-section-fields" style={{ marginTop: 12 }}>
                              {s.fields.map(f => (
                                f.type === 'row' ? (
                                  <div key={f.id} className="a3-row" style={{ display: 'flex', gap: 12 }}>
                                    {(f.children||[]).map(c => (
                                      <div key={c.id} style={{ flex: 1 }}>
                                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>{c.label}</label>
                                        <input className="input" type="text" value={c.value || ''} readOnly disabled />
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div key={f.id} className="a3-field" style={{ marginTop: 8 }}>
                                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>{f.label}</label>
                                    <div>
                                      {f.type === 'text' && <input className="input" type="text" value={f.value || ''} readOnly disabled />}
                                      {f.type === 'textarea' && <textarea className="textarea" value={f.value || ''} readOnly disabled rows={3} />}
                                      {f.type === 'dropdown' && (
                                        <select className="input" value={f.value || (f.options && f.options[0]) || ''} disabled>
                                          {(f.options||[]).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                        </select>
                                      )}
                                      {f.type === 'date' && <input className="input" type="date" value={f.value || ''} disabled />}
                                      {f.type === 'number' && (
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                          <button className="btn small" disabled>-</button>
                                          <input className="input" type="number" value={f.value ?? 0} readOnly disabled style={{ width: 100 }} />
                                          <button className="btn small" disabled>+</button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )
                              ))}
                          </div>
                        )}
                      </article>
                    ))}
                  </main>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {generatedJson && (
        <div className="generated-json-block">
          <label className="form-label">Generated JSON</label>
          <textarea readOnly value={generatedJson} rows={12} className="textarea monospace" />
        </div>
      )}

      {/* Refresh Confirmation Modal */}
      {showRefreshConfirm && (
        <div 
          className="modal-backdrop" 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0, 0, 0, 0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 9999 
          }}
          onClick={cancelRefresh}
        >
          <div 
            className="a3-confirm-dialog"
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '480px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              animation: 'slideDown 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div 
                style={{
                  flexShrink: 0,
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}
              >
                ⚠️
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '20px', 
                  fontWeight: 700, 
                  color: '#1e293b' 
                }}>
                  Refresh Form?
                </h3>
                <p style={{ 
                  margin: '0 0 24px 0', 
                  fontSize: '15px', 
                  color: '#64748b', 
                  lineHeight: '1.6' 
                }}>
                  Are you sure you want to refresh the form? All unsaved changes will be lost.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    className="btn form-button cancel-button"
                    onClick={cancelRefresh}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn form-button"
                    onClick={confirmRefresh}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      background: '#94a3b8',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#64748b'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#94a3b8'}
                  >
                    Refresh Form
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default A3Builder;

